/**
 * Meta's campaign report (wave 7b): `/api/v1/analytics/advertising/campaigns` and
 * its `/export` CSV. `analytics.read` to read, `exports.create` for the CSV; both
 * behind the `advertising_meta` flag, off answering `404`.
 *
 * Every purchase and revenue figure here is Meta's own count, never TmT's first
 * party attribution — `docs/architecture/integrations-meta.md` §12.
 */

import type {
  AdvertisingFreshness,
  AdvertisingLevel,
  AdvertisingMetrics,
  AdvertisingQuery,
  AdvertisingReport,
  AdvertisingRow,
  AttributionModel,
  MetaImportRun,
  MetaImportStatus,
  TmtAttributedMetrics,
  TmtUnmatchedPaid,
} from './types'

export interface AdvertisingApi {
  advertisingCampaigns(query: AdvertisingQuery): Promise<AdvertisingReport>
  /** The same report as a CSV file (`exports.create`); a refusal rejects with the API's error. */
  advertisingCampaignsCsv(query: AdvertisingQuery): Promise<Blob>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

interface AdvertisingMetricsOut {
  spend_minor: number
  impressions: number
  clicks: number
  landing_page_views: number
  ctr: number | null
  cpc_minor: number | null
  cpm_minor: number | null
  provider_purchases: number
  provider_purchase_revenue_minor: number
  provider_cpa_minor: number | null
  provider_roas: number | null
}

interface TmtAttributedOut {
  model: AttributionModel
  tmt_attributed: true
  purchases: number
  revenue_minor: number
  new_customers: number
  cpa_minor: number | null
  roas: number | null
  cost_per_new_customer_minor: number | null
}

interface TmtUnmatchedPaidOut {
  purchases: number
  revenue_minor: number
}

interface AdvertisingRowOut {
  id: string
  name: string | null
  status: string | null
  effective_status: string | null
  metrics: AdvertisingMetricsOut
  previous: AdvertisingMetricsOut | null
  tmt: TmtAttributedOut
  tmt_previous: TmtAttributedOut | null
}

interface ImportRunOut {
  started_at: string
  finished_at: string | null
  status: MetaImportStatus
  rows_written: number
  error: string | null
}

export interface AdvertisingFreshnessOut {
  generated_at: string
  last_recent_import: ImportRunOut | null
  provider_lag: boolean
  connected: boolean
  mapped_accounts: number
}

export interface AdvertisingCampaignReportOut {
  source: 'meta'
  start: string
  end: string
  level: AdvertisingLevel
  parent_id: string | null
  compare: 'previous_period' | null
  previous_start: string | null
  previous_end: string | null
  currency: string
  attribution_windows: string[]
  rows: AdvertisingRowOut[]
  totals: AdvertisingMetricsOut
  totals_previous: AdvertisingMetricsOut | null
  tmt_model: AttributionModel
  tmt_lookback_days: number
  tmt_totals: TmtAttributedOut
  tmt_totals_previous: TmtAttributedOut | null
  tmt_unmatched_paid: TmtUnmatchedPaidOut
  as_of: AdvertisingFreshnessOut
}

function metricsFromWire(body: AdvertisingMetricsOut): AdvertisingMetrics {
  return {
    spendMinor: body.spend_minor,
    impressions: body.impressions,
    clicks: body.clicks,
    landingPageViews: body.landing_page_views,
    ctr: body.ctr,
    cpcMinor: body.cpc_minor,
    cpmMinor: body.cpm_minor,
    providerPurchases: body.provider_purchases,
    providerPurchaseRevenueMinor: body.provider_purchase_revenue_minor,
    providerCpaMinor: body.provider_cpa_minor,
    providerRoas: body.provider_roas,
  }
}

function tmtFromWire(body: TmtAttributedOut): TmtAttributedMetrics {
  return {
    model: body.model,
    tmtAttributed: true,
    purchases: body.purchases,
    revenueMinor: body.revenue_minor,
    newCustomers: body.new_customers,
    cpaMinor: body.cpa_minor,
    roas: body.roas,
    costPerNewCustomerMinor: body.cost_per_new_customer_minor,
  }
}

function unmatchedPaidFromWire(body: TmtUnmatchedPaidOut): TmtUnmatchedPaid {
  return { purchases: body.purchases, revenueMinor: body.revenue_minor }
}

function rowFromWire(row: AdvertisingRowOut): AdvertisingRow {
  return {
    id: row.id,
    name: row.name,
    status: row.status,
    effectiveStatus: row.effective_status,
    metrics: metricsFromWire(row.metrics),
    previous: row.previous ? metricsFromWire(row.previous) : null,
    tmt: tmtFromWire(row.tmt),
    tmtPrevious: row.tmt_previous ? tmtFromWire(row.tmt_previous) : null,
  }
}

function runFromWire(run: ImportRunOut | null): MetaImportRun | null {
  if (!run) return null
  return {
    startedAt: run.started_at,
    finishedAt: run.finished_at,
    status: run.status,
    rowsWritten: run.rows_written,
    error: run.error,
  }
}

/** The freshness block every Meta read carries (the campaign report's, the overview's, the reconciliation's). */
export function freshnessFromWire(body: AdvertisingFreshnessOut): AdvertisingFreshness {
  return {
    generatedAt: body.generated_at,
    lastRecentImport: runFromWire(body.last_recent_import),
    providerLag: body.provider_lag,
    connected: body.connected,
    mappedAccounts: body.mapped_accounts,
  }
}

export function advertisingReportFromWire(body: AdvertisingCampaignReportOut): AdvertisingReport {
  return {
    source: body.source,
    start: body.start,
    end: body.end,
    level: body.level,
    parentId: body.parent_id,
    compared: body.compare !== null,
    previousStart: body.previous_start,
    previousEnd: body.previous_end,
    currency: body.currency,
    attributionWindows: body.attribution_windows,
    rows: body.rows.map(rowFromWire),
    totals: metricsFromWire(body.totals),
    totalsPrevious: body.totals_previous ? metricsFromWire(body.totals_previous) : null,
    tmtModel: body.tmt_model,
    tmtLookbackDays: body.tmt_lookback_days,
    tmtTotals: tmtFromWire(body.tmt_totals),
    tmtTotalsPrevious: body.tmt_totals_previous ? tmtFromWire(body.tmt_totals_previous) : null,
    tmtUnmatchedPaid: unmatchedPaidFromWire(body.tmt_unmatched_paid),
    asOf: freshnessFromWire(body.as_of),
  }
}

/**
 * The UI's metric keys are camelCase (`AdvertisingMetrics`); the API's `sort`
 * field and `SORT_FIELDS` are snake_case, the same as every other field on the
 * wire. One conversion, so a `sort` built from a column's own `key` can never
 * silently drift from what the API accepts.
 */
export function wireSortField(field: string): string {
  return field.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function wireSort(sort: string | undefined): string | undefined {
  if (!sort) return sort
  const descending = sort.startsWith('-')
  const field = wireSortField(descending ? sort.slice(1) : sort)
  return descending ? `-${field}` : field
}

/** The query string both the campaign report and its CSV take, so the download is the table on screen. */
export function advertisingSearch(query: AdvertisingQuery): string {
  const params = new URLSearchParams({ from: query.from, to: query.to, level: query.level })
  if (query.compare) params.set('compare', 'previous_period')
  if (query.parentId) params.set('parent_id', query.parentId)
  if (query.q) params.set('q', query.q)
  const sort = wireSort(query.sort)
  if (sort) params.set('sort', sort)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  if (query.model) params.set('model', query.model)
  return params.toString()
}
